/**
 * Navigation policy — the admin/workspace-level controls over how much of the
 * navigation experience a user may customise, and what is forced or forbidden.
 *
 * Admin policy ALWAYS wins over a user preference. The three hard rules the
 * resolver enforces:
 *   1. `hiddenNavItems` are never visible (even if locked, pinned, or in a
 *      profile preset).
 *   2. `lockedVisibleNavItems` are forced visible — but only for a user who
 *      has permission for that item. Policy can't grant access.
 *   3. `allowedWorkProfiles` bounds which profiles a user may select; the
 *      `allow*Customization` flags gate the user-facing controls.
 *
 * Persistence: stored per-tenant as a JSON string in the settings registry
 * key `NAV_POLICY` (tenant-overridable). `coerceNavigationPolicy` is the single
 * sanitiser for any untrusted source (DB JSON, an API body) — it drops unknown
 * nav ids / profiles so a stale or hand-edited value can never widen access or
 * crash a consumer.
 */
import { NAV_ITEM_IDS } from "./nav-catalog";
import { WORK_PROFILES, DEFAULT_WORK_PROFILE, isWorkProfile, type WorkProfile } from "./work-profiles";

export type NavigationPolicy = {
  defaultWorkProfile: WorkProfile;
  allowedWorkProfiles: WorkProfile[];
  lockedVisibleNavItems: string[];
  hiddenNavItems: string[];
  allowUserNavigationCustomization: boolean;
  allowUserStartPageCustomization: boolean;
  allowUserDisplaySizeCustomization: boolean;
};

/**
 * The default policy is fully permissive — it reproduces today's behaviour
 * exactly: every profile selectable, nothing forced or hidden, all user
 * customisation allowed. A tenant that never touches the policy is unaffected.
 */
export const DEFAULT_NAVIGATION_POLICY: NavigationPolicy = {
  defaultWorkProfile: DEFAULT_WORK_PROFILE,
  allowedWorkProfiles: [...WORK_PROFILES],
  lockedVisibleNavItems: [],
  hiddenNavItems: [],
  allowUserNavigationCustomization: true,
  allowUserStartPageCustomization: true,
  allowUserDisplaySizeCustomization: true,
};

const KNOWN_IDS = new Set(NAV_ITEM_IDS);

function sanitiseIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of value) {
    if (typeof v === "string" && KNOWN_IDS.has(v) && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

function sanitiseProfiles(value: unknown): WorkProfile[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<WorkProfile>();
  for (const v of value) {
    if (isWorkProfile(v)) seen.add(v);
  }
  return [...seen];
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

/**
 * Coerce any untrusted value into a valid NavigationPolicy. Unknown nav ids
 * and profiles are dropped; missing fields fall back to the permissive
 * default. An item present in BOTH locked and hidden is left in both — the
 * resolver resolves the conflict (hidden wins), so the policy stays a faithful
 * record of what the admin set.
 */
export function coerceNavigationPolicy(raw: unknown): NavigationPolicy {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_NAVIGATION_POLICY };
  const r = raw as Record<string, unknown>;

  let allowed = sanitiseProfiles(r.allowedWorkProfiles);
  // An empty allow-list would lock the user out of every profile — treat it as
  // "no restriction", matching the default.
  if (allowed.length === 0) allowed = [...WORK_PROFILES];

  let defaultProfile = isWorkProfile(r.defaultWorkProfile) ? r.defaultWorkProfile : DEFAULT_WORK_PROFILE;
  // The default must itself be selectable.
  if (!allowed.includes(defaultProfile)) defaultProfile = allowed[0];

  return {
    defaultWorkProfile: defaultProfile,
    allowedWorkProfiles: allowed,
    lockedVisibleNavItems: sanitiseIds(r.lockedVisibleNavItems),
    hiddenNavItems: sanitiseIds(r.hiddenNavItems),
    allowUserNavigationCustomization: asBool(r.allowUserNavigationCustomization, true),
    allowUserStartPageCustomization: asBool(r.allowUserStartPageCustomization, true),
    allowUserDisplaySizeCustomization: asBool(r.allowUserDisplaySizeCustomization, true),
  };
}

/** Parse a JSON string (the DB representation) into a policy; never throws. */
export function parseNavigationPolicy(json: string | null | undefined): NavigationPolicy {
  if (!json) return { ...DEFAULT_NAVIGATION_POLICY };
  try {
    return coerceNavigationPolicy(JSON.parse(json));
  } catch {
    return { ...DEFAULT_NAVIGATION_POLICY };
  }
}

/** Serialise a policy for storage. */
export function serializeNavigationPolicy(policy: NavigationPolicy): string {
  return JSON.stringify(coerceNavigationPolicy(policy));
}
